import { __decorate } from 'tslib';
import { NgTemplateOutlet } from '@angular/common';
import * as i0 from '@angular/core';
import { numberAttribute, booleanAttribute, Component, ViewEncapsulation, Input, NgModule } from '@angular/core';
import { Subject, BehaviorSubject, ReplaySubject, timer } from 'rxjs';
import { startWith, distinctUntilChanged, switchMap, debounce, takeUntil } from 'rxjs/operators';
import * as i1 from 'ng-zorro-antd/core/config';
import { WithConfig } from 'ng-zorro-antd/core/config';
import * as i2 from '@angular/cdk/bidi';
const _c0 = ["*"];
function NzSpinComponent_ng_template_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 2);
    i0.ɵɵelement(1, "i", 3)(2, "i", 3)(3, "i", 3)(4, "i", 3);
    i0.ɵɵelementEnd();
  }
}
function NzSpinComponent_Conditional_2_ng_template_2_Template(rf, ctx) {}
function NzSpinComponent_Conditional_2_Conditional_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 6);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r0.nzTip);
  }
}
function NzSpinComponent_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div")(1, "div", 4);
    i0.ɵɵtemplate(2, NzSpinComponent_Conditional_2_ng_template_2_Template, 0, 0, "ng-template", 5)(3, NzSpinComponent_Conditional_2_Conditional_3_Template, 2, 1, "div", 6);
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    const defaultTemplate_r2 = i0.ɵɵreference(1);
    i0.ɵɵadvance();
    i0.ɵɵclassProp("ant-spin-rtl", ctx_r0.dir === "rtl")("ant-spin-spinning", ctx_r0.isLoading)("ant-spin-lg", ctx_r0.nzSize === "large")("ant-spin-sm", ctx_r0.nzSize === "small")("ant-spin-show-text", ctx_r0.nzTip);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r0.nzIndicator || defaultTemplate_r2);
    i0.ɵɵadvance();
    i0.ɵɵconditional(ctx_r0.nzTip ? 3 : -1);
  }
}
function NzSpinComponent_Conditional_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 7);
    i0.ɵɵprojection(1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵclassProp("ant-spin-blur", ctx_r0.isLoading);
  }
}
const NZ_CONFIG_MODULE_NAME = 'spin';
let NzSpinComponent = /*#__PURE__*/(() => {
  class NzSpinComponent {
    constructor(nzConfigService, cdr, directionality) {
      this.nzConfigService = nzConfigService;
      this.cdr = cdr;
      this.directionality = directionality;
      this._nzModuleName = NZ_CONFIG_MODULE_NAME;
      this.nzIndicator = null;
      this.nzSize = 'default';
      this.nzTip = null;
      this.nzDelay = 0;
      this.nzSimple = false;
      this.nzSpinning = true;
      this.destroy$ = new Subject();
      this.spinning$ = new BehaviorSubject(this.nzSpinning);
      this.delay$ = new ReplaySubject(1);
      this.isLoading = false;
      this.dir = 'ltr';
    }
    ngOnInit() {
      const loading$ = this.delay$.pipe(startWith(this.nzDelay), distinctUntilChanged(), switchMap(delay => {
        if (delay === 0) {
          return this.spinning$;
        }
        return this.spinning$.pipe(debounce(spinning => timer(spinning ? delay : 0)));
      }), takeUntil(this.destroy$));
      loading$.subscribe(loading => {
        this.isLoading = loading;
        this.cdr.markForCheck();
      });
      this.nzConfigService.getConfigChangeEventForComponent(NZ_CONFIG_MODULE_NAME).pipe(takeUntil(this.destroy$)).subscribe(() => this.cdr.markForCheck());
      this.directionality.change?.pipe(takeUntil(this.destroy$)).subscribe(direction => {
        this.dir = direction;
        this.cdr.detectChanges();
      });
      this.dir = this.directionality.value;
    }
    ngOnChanges(changes) {
      const {
        nzSpinning,
        nzDelay
      } = changes;
      if (nzSpinning) {
        this.spinning$.next(this.nzSpinning);
      }
      if (nzDelay) {
        this.delay$.next(this.nzDelay);
      }
    }
    ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
    }
    static {
      this.ɵfac = function NzSpinComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NzSpinComponent)(i0.ɵɵdirectiveInject(i1.NzConfigService), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i2.Directionality));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NzSpinComponent,
        selectors: [["nz-spin"]],
        hostVars: 2,
        hostBindings: function NzSpinComponent_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵclassProp("ant-spin-nested-loading", !ctx.nzSimple);
          }
        },
        inputs: {
          nzIndicator: "nzIndicator",
          nzSize: "nzSize",
          nzTip: "nzTip",
          nzDelay: [2, "nzDelay", "nzDelay", numberAttribute],
          nzSimple: [2, "nzSimple", "nzSimple", booleanAttribute],
          nzSpinning: [2, "nzSpinning", "nzSpinning", booleanAttribute]
        },
        exportAs: ["nzSpin"],
        standalone: true,
        features: [i0.ɵɵInputTransformsFeature, i0.ɵɵNgOnChangesFeature, i0.ɵɵStandaloneFeature],
        ngContentSelectors: _c0,
        decls: 4,
        vars: 2,
        consts: [["defaultTemplate", ""], [1, "ant-spin-container", 3, "ant-spin-blur"], [1, "ant-spin-dot", "ant-spin-dot-spin"], [1, "ant-spin-dot-item"], [1, "ant-spin"], [3, "ngTemplateOutlet"], [1, "ant-spin-text"], [1, "ant-spin-container"]],
        template: function NzSpinComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵtemplate(0, NzSpinComponent_ng_template_0_Template, 5, 0, "ng-template", null, 0, i0.ɵɵtemplateRefExtractor)(2, NzSpinComponent_Conditional_2_Template, 4, 12, "div")(3, NzSpinComponent_Conditional_3_Template, 2, 2, "div", 1);
          }
          if (rf & 2) {
            i0.ɵɵadvance(2);
            i0.ɵɵconditional(ctx.isLoading ? 2 : -1);
            i0.ɵɵadvance();
            i0.ɵɵconditional(!ctx.nzSimple ? 3 : -1);
          }
        },
        dependencies: [NgTemplateOutlet],
        encapsulation: 2
      });
    }
  }
  __decorate([WithConfig()], NzSpinComponent.prototype, "nzIndicator", void 0);
  return NzSpinComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzSpinModule = /*#__PURE__*/(() => {
  class NzSpinModule {
    static {
      this.ɵfac = function NzSpinModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NzSpinModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: NzSpinModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
    }
  }
  return NzSpinModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NzSpinComponent, NzSpinModule };
